import React, { createContext, useContext, useState, useEffect } from 'react'
import { login as apiLogin } from '../system/apitools'

export interface IAccountContext {
  initialized: boolean
  user: { email: string } | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

function decodeJwtPayload(token: string): { email: string; exp: number } | null {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const AccountContext = createContext<IAccountContext>({
  initialized: false,
  user: null,
  token: null,
  login: async () => {},
  logout: () => {},
})

export const useAccount = () => useContext(AccountContext)

export const AccountContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [initialized, setInitialized] = useState(false)
  const [user, setUser] = useState<{ email: string } | null>(null)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const stored = localStorage.getItem('forum_token')
    if (stored) {
      const payload = decodeJwtPayload(stored)
      if (payload && payload.exp * 1000 > Date.now()) {
        setToken(stored)
        setUser({ email: payload.email })
      } else {
        localStorage.removeItem('forum_token')
      }
    }
    setInitialized(true)
  }, [])

  const login = async (email: string, password: string) => {
    const { token: newToken } = await apiLogin(email, password)
    localStorage.setItem('forum_token', newToken)
    const payload = decodeJwtPayload(newToken)
    setToken(newToken)
    setUser(payload ? { email: payload.email } : null)
  }

  const logout = () => {
    localStorage.removeItem('forum_token')
    setToken(null)
    setUser(null)
    window.location.href = '/login'
  }

  return (
    <AccountContext.Provider value={{ initialized, user, token, login, logout }}>
      {children}
    </AccountContext.Provider>
  )
}
